/**
 * Weekly schedules → dated events.
 *
 * masstimes.org lists services by weekday and wall-clock time. The planner
 * wants concrete instants, so each church's schedule is expanded over the trip's
 * dates in the church's own time zone (a trip can cross zones and DST changes).
 * This is also where each Mass gets its kind, the time allotted to it, and the
 * day of obligation it counts for.
 */

import type { Church, Service, ServiceCategory, ServiceKind } from "@viamissa/masstimes";
import { addDays, dateKeyAt, dateRange, instantOf, MINUTE, parseDateKey, weekdayOf, type DateKey } from "@viamissa/time";

/**
 * Minutes allotted to attend a Mass, by kind: Sunday, vigil and holy day 1 h;
 * weekday 30 min. Easter, Christmas and other high days override this; see
 * {@link highDay}.
 */
export const MASS_MINUTES: Record<"sunday" | "vigil" | "weekday" | "holyday" | "holyday-vigil", number> = {
  sunday: 60,
  vigil: 60,
  holyday: 60,
  "holyday-vigil": 60,
  weekday: 30,
};

export type Event = {
  /** Unique per church, service and date. */
  id: string;
  churchId: string;
  kind: ServiceKind;
  category: ServiceCategory;
  /** Absolute start, epoch ms. */
  start: number;
  /** Absolute end for services listed with one (Confession, Adoration), else null. */
  end: number | null;
  /** Minutes allotted for a Mass; 0 for other services. */
  minutes: number;
  /** Local calendar date where the service happens. */
  date: DateKey;
  /**
   * The day of obligation this Mass fulfils: the Sunday for a Saturday vigil,
   * the holy day for a holy-day vigil or Mass, the date itself for a Sunday;
   * null for weekday Masses and non-Mass services.
   */
  obligation: DateKey | null;
  language: string | null;
  comment: string | null;
  /** Set when an assumption was made the traveller should check by phone. */
  caveat: string | null;
  /** "Easter Vigil", "Christmas", … when the Mass falls on a high day. */
  occasion: string | null;
};

const pad = (value: number) => String(value).padStart(2, "0");

/**
 * Holy days of obligation in the United States (Latin Church).
 *
 * Mary, Mother of God (Jan 1), the Assumption (Aug 15) and All Saints (Nov 1)
 * lose the obligation when they fall on a Saturday or Monday. The Immaculate
 * Conception and Christmas always bind; when either falls on a Sunday it is a
 * Sunday anyway. Ascension Thursday is left out because nearly every US
 * province moves it to the following Sunday.
 */
export function holyDaysOfObligation(year: number): DateKey[] {
  const days: DateKey[] = [];
  const add = (month: number, day: number, abrogable: boolean) => {
    const key = `${year}-${pad(month)}-${pad(day)}`;
    const weekday = weekdayOf(key);
    if (weekday === 0) return; // already a Sunday
    if (abrogable && (weekday === 6 || weekday === 1)) return;
    days.push(key);
  };
  add(1, 1, true);
  add(8, 15, true);
  add(11, 1, true);
  add(12, 8, false);
  add(12, 25, false);
  return days;
}

/** Gregorian Easter Sunday (anonymous Gregorian algorithm). */
export function easterSunday(year: number): DateKey {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return `${year}-${pad(month)}-${pad(day)}`;
}

export type HighDay = { name: string; minutes: number; caveat: string | null };

/**
 * Days whose Masses run long, and how long to allot. Keyed by the day a Mass
 * belongs to (a Saturday vigil belongs to its Sunday), so the Easter Vigil is
 * the vigil of Easter. Edit this table to tune the allotments.
 */
export function highDay(day: DateKey, vigil = false): HighDay | null {
  const { year, month, day: dom } = parseDateKey(day);
  const easter = easterSunday(year);
  if (day === easter && vigil) {
    return { name: "Easter Vigil", minutes: 180, caveat: "The Easter Vigil starts after nightfall, often later than the usual Saturday time; call to confirm" };
  }
  if (day === easter) return { name: "Easter Sunday", minutes: 90, caveat: null };
  if (day === addDays(easter, -7)) return { name: "Palm Sunday", minutes: 90, caveat: null };
  if (day === addDays(easter, -3)) {
    return { name: "Holy Thursday", minutes: 90, caveat: "Most parishes have only the evening Mass of the Lord's Supper on Holy Thursday; call to confirm" };
  }
  if (day === addDays(easter, 49)) return { name: "Pentecost", minutes: 75, caveat: null };
  if (month === 12 && dom === 25) return { name: "Christmas", minutes: 90, caveat: vigil ? "Christmas Eve times differ from the usual schedule; call to confirm" : null };
  return null;
}

/** Good Friday and Holy Saturday: no Mass is celebrated before the Easter Vigil. */
export function isMasslessDay(date: DateKey): boolean {
  const easter = easterSunday(parseDateKey(date).year);
  return date === addDays(easter, -2) || date === addDays(easter, -1);
}

export function isHolyDay(date: DateKey): boolean {
  return holyDaysOfObligation(parseDateKey(date).year).includes(date);
}

function obligationFor(kind: ServiceKind, date: DateKey): DateKey | null {
  switch (kind) {
    case "sunday":
    case "holyday":
      return date;
    case "vigil":
    case "holyday-vigil":
      return addDays(date, 1);
    default:
      return null;
  }
}

function minutesFor(kind: ServiceKind): number {
  return kind in MASS_MINUTES ? MASS_MINUTES[kind as keyof typeof MASS_MINUTES] : 0;
}

/** Which of a church's services happen on a date, with the kind they have that day. */
function servicesOn(church: Church, date: DateKey): { service: Service; kind: ServiceKind; caveat: string | null }[] {
  const weekday = weekdayOf(date);
  const holy = isHolyDay(date);
  const eve = isHolyDay(addDays(date, 1));
  const hasHolySchedule = church.services.some((service) => service.kind === "holyday");
  const out: { service: Service; kind: ServiceKind; caveat: string | null }[] = [];

  for (const service of church.services) {
    if (!service.start || service.perpetual) continue;
    const onWeekday = service.weekday === null || service.weekday === weekday;

    switch (service.kind) {
      case "holyday":
        if (holy && onWeekday) out.push({ service, kind: "holyday", caveat: null });
        break;
      case "holyday-vigil":
        if (eve && onWeekday) out.push({ service, kind: "holyday-vigil", caveat: null });
        break;
      case "weekday":
        if (service.weekday !== weekday) break;
        if (holy && hasHolySchedule) break; // the holy-day schedule replaces it
        if (holy) {
          out.push({ service, kind: "holyday", caveat: "Holy day: this church lists no holy-day schedule, so this is its usual weekday time" });
        } else {
          out.push({ service, kind: "weekday", caveat: null });
        }
        break;
      default:
        if (service.weekday === weekday) out.push({ service, kind: service.kind, caveat: null });
    }
  }
  return out;
}

const ORDINALS: [RegExp, number][] = [
  [/^(1st|first)$/i, 1],
  [/^(2nd|second)$/i, 2],
  [/^(3rd|third)$/i, 3],
  [/^(4th|fourth)$/i, 4],
  [/^(5th|fifth)$/i, 5],
  [/^last$/i, -1],
];

/**
 * Whether a listing whose comment names weeks of the month ("First Fri.",
 * "2nd. Fri", "First Saturdays only", "Except 1st Saturdays") falls on `date`.
 * Comments that name no week ("Student Mass", "Rosary") always do.
 */
export function occursOn(comment: string | null, date: DateKey): boolean {
  if (!comment) return true;
  const words = comment.split(/[^A-Za-z0-9]+/).filter(Boolean);
  const weeks = words.flatMap((word) => ORDINALS.filter(([pattern]) => pattern.test(word)).map(([, week]) => week));
  if (weeks.length === 0) return true;
  const { year, month, day } = parseDateKey(date);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const matches = weeks.some((week) => (week === -1 ? day + 7 > lastDay : Math.ceil(day / 7) === week));
  return /\b(except|not|no)\b/i.test(comment) ? !matches : matches;
}

export type ExpandOptions = {
  church: Church;
  timeZone: string;
  /** Expand services starting in [from, to), epoch ms. */
  from: number;
  to: number;
};

/** Every dated service at one church inside a window, in start order. */
export function expandChurch(options: ExpandOptions): Event[] {
  const { church, timeZone, from, to } = options;
  const firstDate = addDays(dateKeyAt(from, timeZone), -1);
  const lastDate = addDays(dateKeyAt(to, timeZone), 1);
  const events: Event[] = [];

  for (const date of dateRange(firstDate, lastDate)) {
    const { year, month, day } = parseDateKey(date);
    for (const { service, kind, caveat } of servicesOn(church, date)) {
      if (!occursOn(service.comment, date)) continue;
      const startClock = service.start!;
      const start = instantOf({ year, month, day, hour: startClock.hour, minute: startClock.minute }, timeZone);
      if (start < from || start >= to) continue;
      let end: number | null = null;
      if (service.end) {
        end = instantOf({ year, month, day, hour: service.end.hour, minute: service.end.minute }, timeZone);
        if (end <= start) end = null;
      }
      const obligation = obligationFor(kind, date);
      let minutes = minutesFor(kind);
      let note = caveat;
      let occasion: string | null = null;
      if (service.category === "mass") {
        const isVigil = kind === "vigil" || kind === "holyday-vigil";
        // A vigil belongs to the next day; anything else to its own date.
        if (!isVigil && isMasslessDay(date)) continue;
        const high = highDay(isVigil ? (obligation ?? date) : date, isVigil);
        if (high) {
          minutes = high.minutes;
          occasion = high.name;
        }
        if (high?.caveat) note = note ? `${note}. ${high.caveat}` : high.caveat;
      }
      events.push({
        id: `${church.id}:${date}:${pad(startClock.hour)}${pad(startClock.minute)}:${kind}`,
        churchId: church.id,
        kind,
        category: service.category,
        start,
        end,
        minutes,
        date,
        obligation,
        language: service.language,
        comment: service.comment,
        caveat: note,
        occasion,
      });
    }
  }
  // The same Mass can be listed twice (e.g. under two languages); keep one.
  const unique = new Map(events.map((event) => [event.id, event]));
  return [...unique.values()].sort((a, b) => a.start - b.start);
}

/** Mass events only. */
export const isMass = (event: Event): boolean => event.category === "mass";

/** When the time allotted to a Mass runs out, epoch ms. */
export function allottedEnd(event: Event): number {
  return event.start + event.minutes * MINUTE;
}
